import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, ShieldCheck } from "lucide-react";

export default function Checkout() {
  const { bookingId } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/bookings/${bookingId}`).then((r) => setBooking(r.data)).catch(() => toast.error("Booking not found"));
  }, [bookingId]);

  const pay = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        booking_id: bookingId,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create checkout session.");
      setLoading(false);
    }
  };

  if (!booking) return <div className="pt-24 text-center text-slate-500">Loading booking…</div>;

  return (
    <div className="pt-24 pb-16 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <div className="overline">Step 5 of 6</div>
        <h1 className="mt-2 text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
          Confirm & pay for parts
        </h1>
        <p className="mt-2 text-slate-600">
          Ticket <span className="font-mono font-semibold text-slate-900">{booking.ticket_code}</span> — you're
          only paying for parts now. Labor is due at pickup.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 p-6 md:p-8 rounded-2xl border border-slate-200 bg-white">
          <div className="overline mb-3">Your repair</div>
          <div className="text-sm space-y-1 text-slate-700">
            <div><span className="text-slate-500">Device:</span> {booking.brand} {booking.model_name} <span className="text-slate-400">({booking.device_type})</span></div>
            <div><span className="text-slate-500">Drop-off:</span> {booking.dropoff_date} at {booking.dropoff_time}</div>
            <div><span className="text-slate-500">Contact:</span> {booking.customer.name} · {booking.customer.email} · {booking.customer.phone}</div>
          </div>

          <div className="overline mt-6 mb-3">Parts we'll be ordering</div>
          {booking.parts_needed.length === 0 ? (
            <p className="text-sm text-slate-500">Diagnostics only — no parts ordered.</p>
          ) : (
            <ul className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden">
              {booking.parts_needed.map((p) => (
                <li key={p.sku} className="flex items-center justify-between p-3 bg-slate-50/40">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{p.name}</div>
                    <div className="text-xs text-slate-500 font-mono">{p.sku} · for {p.for_model}</div>
                  </div>
                  <div className="font-mono text-sm text-slate-900">${p.estimated_cost.toFixed(2)}</div>
                </li>
              ))}
            </ul>
          )}
          {booking.photos && booking.photos.length > 0 && (
            <>
              <div className="overline mt-6 mb-3">Photos you shared</div>
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-2" data-testid="checkout-photos-block">
                {booking.photos.map((p) => (
                  <a
                    key={p.id}
                    data-testid={`checkout-photo-${p.id}`}
                    href={`${API}/photos/${p.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="aspect-square rounded-lg border border-slate-200 overflow-hidden bg-slate-100 block hover:border-amber-500"
                  >
                    <img src={`${API}/photos/${p.id}`} alt={p.filename} className="w-full h-full object-cover" />
                  </a>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="md:col-span-1 p-6 md:p-8 rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="overline mb-3">Order summary</div>
          <div className="space-y-2 text-sm">
            <Row label="Parts" value={booking.quote.parts_subtotal} />
            <Row label="Labor (due at pickup)" value={booking.quote.labor_total} />
            {(booking.quote.tax_total || 0) > 0 && <Row label="Estimated Sales Tax" value={booking.quote.tax_total} />}
            <div className="border-t border-slate-200 my-2" />
            <Row label="Parts payment due online" value={booking.quote.parts_total + (booking.quote.tax_total || 0)} bold />
            <div className="text-xs text-slate-500">Labor of ${booking.quote.labor_total.toFixed(2)} is due at pickup.</div>
          </div>
          <p className="mt-3 text-[11px] text-slate-500">
            Parts are ordered after payment according to our <a href="/policy/parts-cancellation" target="_blank" rel="noopener noreferrer" className="text-slate-900 underline">Parts Order &amp; Cancellation Policy</a>.
          </p>
          <button
            data-testid="btn-pay-parts-upfront"
            onClick={pay}
            disabled={loading || booking.parts_paid}
            className="mt-5 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-amber-600 text-white font-semibold hover:bg-amber-500 transition disabled:opacity-40"
          >
            <CreditCard className="w-4 h-4" />
            {booking.parts_paid ? "Already paid" : loading ? "Redirecting…" : `Pay $${booking.quote.parts_total.toFixed(2)} with Stripe`}
          </button>
          <div className="mt-4 flex items-center gap-2 text-xs text-slate-500">
            <ShieldCheck className="w-4 h-4 text-slate-400" /> Secured by Stripe · test card 4242 4242 4242 4242
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className="flex items-center justify-between">
      <span className={bold ? "font-semibold text-slate-900" : "text-slate-600"}>{label}</span>
      <span className={`font-mono ${bold ? "font-bold text-slate-900" : "text-slate-800"}`}>${value.toFixed(2)}</span>
    </div>
  );
}
